import React, { useLayoutEffect } from 'react'
import { Button } from 'core/components/button'
import { TextInput } from 'core/components/form/text-input'
import { useForm } from 'core/hooks/form'
import { requiredMessage, CellPhoneMask } from 'core/constants'
import { ErrorMessage } from 'core/components/form/error-message'
import { isCPF, isCNPJ, isEmail, isOnlyNumbers } from 'core/helpers/validate'
import { Field } from 'core/components/form/field'
import { DateInput } from 'core/components/form/date-input'
import { API } from 'service'
import { Logo } from '../logo'
import { isValidDate } from 'core/helpers/date'

function register(customer, route, form, global) {
	API.add('customer', customer)
		.then(() => {
			global.modal.alert('Cadastro realizado com sucesso.', () =>
				route.history.replace(`/${customer.city.name}/login`),
			)
		})
		.catch(err => form.setErrors({ global: err.message }))
}

export const RegisterForm = ({ route, global }) => {
	const form = useForm(
		{
			initialEntity: {
				id: 0,
				cpfCnpj: '',
				name: '',
				birthDate: null,
				phone: '',
				email: '',
				password: '',
				passwordCheck: '',
				city: {},
				cityId: 0,
			},
			validate: values => {
				const errors = {}

				if (!values.name) {
					errors.name = requiredMessage
				}

				if (!values.cpfCnpj) {
					errors.cpfCnpj = requiredMessage
				} else if (!isCPF(values.cpfCnpj) && !isCNPJ(values.cpfCnpj)) {
					errors.cpfCnpj = 'Digite um CPF/CNPJ válido.'
				}

				if (values.birthDate && !isValidDate(values.birthDate)) {
					errors.birthDate = 'Digite uma data de nascimento válida.'
				}
			
				if (!values.phone) {
					errors.phone = requiredMessage
				}

				if (!values.email) {
					errors.email = requiredMessage
				} else if (!isEmail(values.email)) {
					errors.email = 'Digite um endereço de e-mail válido.'
				}

				if (!values.password && values.id === 0) {
					errors.password = requiredMessage
				}

				if (!isOnlyNumbers(values.password) && values.password && values.id === 0) {
					errors.password = 'A senha deve conter apenas números.'
				}

				if (!values.passwordCheck && values.id === 0) {
					errors.passwordCheck = requiredMessage
				} else if (values.password !== values.passwordCheck) {
					errors.passwordCheck = 'Senhas não coincidem'
				}

				return errors
			},
		},
		route,
	)

	const { cities, loading, modal } = global
	const { history, match } = route
	const { city: cityName } = match.params
	const { city } = form.entity
	const { entity, errors, touched, handleChange, submitting } = form

	useLayoutEffect(() => {
		if (cities.length > 0 && !!cityName) {
			const selected = cities.find(c => c.name === cityName.toLowerCase())

			if (selected) {
				form.setValues({ city: selected, cityId: selected.id })
				return
			}

			modal.alert('Cidade inválida ou não encontrada!')
			history.replace('/login')
		}
		// eslint-disable-next-line
	}, [cities, cityName, loading])

	return (
		<div className="login-wrapper">
			<div className="register-box">
				<div className="logo">
					<Logo isLogin />
				</div>

				<div className="login-title">
					<h3>Cadastro</h3>
					<button
						onClick={ev => {
							ev.preventDefault()
							form.resetForm()
							history.push('/login')
						}}
						type="button"
					>
						{`${city.fullName} - ${city.state}`}
					</button>
				</div>

				<form
					className="login-form"
					onSubmit={ev => {
						ev.preventDefault()
						form.handleSubmit(ent => register(ent, route, form, global))
					}}
				>
					<div>
						<Field label="Nome">
							<TextInput
								autoFocus
								customClassName="form-control-lg"
								meta={{
									error: errors.name,
									touched: touched.name,
								}}
								onChange={(name, type) =>
									handleChange({
										path: 'name',
										type,
										values: { name },
									})
								}
								value={entity.name}
							/>
						</Field>

						<div className="row">
							<div className="col-lg">
								<Field label="CPF/CNPJ">
									<TextInput
										customClassName="form-control-lg"
										meta={{
											error: errors.cpfCnpj,
											touched: touched.cpfCnpj,
										}}
										onChange={(cpfCnpj, type) =>
											handleChange({
												path: 'cpfCnpj',
												type,
												values: { cpfCnpj },
											})
										}
										value={entity.cpfCnpj}
									/>
								</Field>
							</div>
						</div>

						<div className="row">
							<div className="col-lg">
								<Field label="Data de Nascimento">
									<DateInput
										customClassName="form-control-lg"
										meta={{
											error: errors.birthDate,
											touched: touched.birthDate,
										}}
										onChange={(birthDate, type) =>
											form.handleChange({
												path: 'birthDate',
												type,
												values: { birthDate },
											})
										}
										value={entity.birthDate}
									/>
								</Field>
							</div>

							<div className="col-lg">
								<Field label="Telefone">
									<TextInput
										customClassName="form-control-lg"
										icon="fas fa-phone"
										mask={CellPhoneMask}
										meta={{
											error: errors.phone,
											touched: touched.phone,
										}}
										onChange={(phone, type) =>
											handleChange({
												path: 'phone',
												type,
												values: { phone },
											})
										}
										type="tel"
										value={entity.phone}
									/>
								</Field>
							</div>
						</div>

						<Field label="E-mail">
							<TextInput
								customClassName="form-control-lg"
								icon="fas fa-at"
								meta={{
									error: errors.email,
									touched: touched.email,
								}}
								onChange={(email, type) =>
									handleChange({
										path: 'email',
										type,
										values: { email },
									})
								}
								type="email"
								value={entity.email}
							/>
						</Field>

						<div className="row">
							<div className="col-lg">
								<Field label="Senha">
									<TextInput
										customClassName="form-control-lg"
										meta={{
											error: errors.password,
											touched: touched.password,
										}}
										onChange={(password, type) =>
											handleChange({
												path: 'password',
												type,
												values: { password },
											})
										}
										type="password"
										value={entity.password}
									/>
								</Field>
							</div>

							<div className="col-lg">
								<Field label="Confirme a Senha">
									<TextInput
										acceptEnter
										customClassName="form-control-lg"
										meta={{
											error: errors.passwordCheck,
											touched: touched.passwordCheck,
										}}
										onChange={(passwordCheck, type) =>
											handleChange({
												path: 'passwordCheck',
												type,
												values: { passwordCheck },
											})
										}
										type="password"
										value={entity.passwordCheck}
									/>
								</Field>
							</div>
						</div>
					</div>

					<div className="login-actions">
						<Button
							customClassName="btn-primary btn-lg btn-block"
							icon="fas fa-sign-in-alt"
							loading={submitting}
							title="Cadastrar"
						/>
					</div>

					<ErrorMessage error={errors.global} />
				</form>
			</div>
		</div>
	)
}
